import { useMemo, useCallback, useState, useEffect } from 'react';

import configFieldPermissions from '../config/fieldPermissions.CONFIG.json';
import draftFieldPermissions from '../config/fieldPermissions.DRAFT.json';
import siteAssessmentFieldPermissions from '../config/fieldPermissions.SITE_ASSESSMENT.json';
import { useAuth } from '../contexts/AuthContext';
import { useRequestStage } from '../contexts/RequestStageContext';
import { fieldPermissionService } from '../services/auth/fieldPermissionService';
import { roleService } from '../services/auth/roleService';
import { teamInformationService } from '../services/teamInformationService';
import { AppRole } from '../types/auth';
import { FieldAccessResult } from '../types/fieldPermissions';

type StagePermissionMatrix = Record<
  string,
  Record<string, Record<string, string>>
>;

/**
 * The stages a request can be sitting in while somebody has it open and could press Edit.
 * PROCESSING and CLOSED are left out because no edit session can exist in either — every
 * role is `-` on `Button - Save` in both. Including them would not change the set below
 * either, and `deliverablesFallback.test.ts` asserts that rather than asking a reader to
 * take it on trust.
 */
const OPEN_STAGE_MATRICES: StagePermissionMatrix[] = [
  draftFieldPermissions,
  siteAssessmentFieldPermissions,
  configFieldPermissions,
];

/**
 * Every role the matrices mark `W` on `Deliverables > Deliverables` at some open stage,
 * read out of the bundled JSON at module load rather than typed out here, so it cannot
 * drift from the matrix it is supposed to mirror.
 */
export const DELIVERABLES_FALLBACK_ROLES: readonly string[] = Array.from(
  new Set(
    OPEN_STAGE_MATRICES.flatMap(matrix =>
      Object.entries(matrix.Deliverables?.Deliverables ?? {})
        .filter(([, permission]) => permission === 'W')
        .map(([role]) => role),
    ),
  ),
);

/** The stage matrices, by the canonical stage name a request actually carries. */
const MATRIX_BY_STAGE: Record<string, StagePermissionMatrix> = {
  DRAFT: draftFieldPermissions,
  INITIAL: draftFieldPermissions,
  PROCESSING: draftFieldPermissions,
  SITE_ASSESSMENT: siteAssessmentFieldPermissions,
  CONFIG: configFieldPermissions,
  CONFIGURATION: configFieldPermissions,
};

/**
 * The roles the matrix marks `W` on `Deliverables > Deliverables` **at one stage**.
 *
 * `DELIVERABLES_FALLBACK_ROLES` above is a union across every open stage, which is the
 * defect: SITE_ASSESSMENT and CONFIG grant `W` to nine roles, DRAFT grants it to two, so the
 * union answered true at DRAFT for eight roles that matrix denies. It held only because
 * those roles cannot enter edit mode at DRAFT — a second mechanism doing the work this one
 * claimed to do. Reading the request's own stage removes the dependency.
 * (MR !489 review, batch 2 change 2.4.)
 */
const deliverablesWritableRolesAtStage = (
  requestStage: string | null | undefined,
): readonly string[] => {
  const matrix =
    MATRIX_BY_STAGE[(requestStage ?? 'DRAFT').trim().toUpperCase()] ??
    // An unknown or CLOSED stage grants nobody: CLOSED is `R` for every role, and an
    // unrecognised value must not silently fall back to the most permissive matrix.
    undefined;
  if (!matrix) return [];

  return Object.entries(matrix.Deliverables?.Deliverables ?? {})
    .filter(([, permission]) => permission === 'W')
    .map(([role]) => role);
};

/**
 * Deliverables write access as far as this hook can work it out with no stage in hand.
 *
 * Reached only when no stage is available. `RequestDetail.tsx`'s first `useFormPermissions`
 * call runs above its own `RequestStageProvider` and before `request` resolves, so
 * `effectiveRequestStage` is undefined for that instance's entire lifetime and the stage-aware
 * `checkFieldAccess({ section: 'Deliverables' })` further down never runs on it.
 *
 * It answers from the roles the matrix marks `W` **at the stage it is given**. An absent
 * stage reads as DRAFT, the most restrictive open stage, so an unknown stage fails closed.
 *
 * It used to answer from a union across every open stage, which over-granted at DRAFT to the
 * seven roles that matrix marks `-`. A four-link argument used to live here explaining why
 * that was unreachable rather than merely unlikely: the section needed edit mode, edit mode
 * needed `canShowEdit`, `canShowEdit` resolved to `Button - Save` at the real stage, and
 * every over-granted role was `-` or `R` there. The argument held, but it made a second
 * mechanism responsible for this one's correctness, so this function was only safe as long as
 * nobody changed the Edit button. Asking about the stage removes the dependency, and with it
 * the need for the argument. (MR !489 review, batch 2 change 2.4.)
 *
 * On Request Detail this is now a genuine last resort rather than that screen's whole answer:
 * a second `useFormPermissions` call there supplies the stage once the request resolves, so
 * the section's access is decided by `fieldPermissionService.checkFieldAccess` instead.
 *
 * Reads `userRoles` directly instead of `roleService.hasAnyRole`: `src/setupTests.ts` mocks
 * that method as `() => false` for the whole client suite, so a predicate routed through it
 * is dead in every test regardless of the user under test.
 */
export const canFallBackToEditingDeliverables = (
  userRoles: readonly string[] | null | undefined,
  partnerContributorAccessLevel?: 'read' | 'read_write' | undefined,
  requestStage?: string | null,
): boolean => {
  const writableRoles = deliverablesWritableRolesAtStage(requestStage);
  if (userRoles?.some(role => writableRoles.includes(role))) {
    return true;
  }

  /**
   * A read_write Partner Contributor reaches this line, and must be granted.
   *
   * The union above is built from literal `W` cells, and Partner Contributor is never `W`
   * on Deliverables: it is `-` at Draft and `R` at the two open stages. But the matrix cell
   * is not what governs a Partner Contributor. `fieldPermissionService` elevates a
   * read_write PC to writable on every ordinary field regardless of their cell, carving out
   * only `Button - Delete` and `Button - Submit`. Deliverables is an ordinary field, so
   * leaving it out of that elevation is what produced the reported defect: Edit was offered
   * (elevated `Button - Save`) while the section stayed read-only (un-elevated cell).
   *
   * The safety argument for the union still holds here, unchanged. A read PC never gets
   * this far, because `useButtonPermissions` returns false for any access level other than
   * read_write before it consults the config at all, so they cannot enter edit mode and the
   * section never leaves read-only for them.
   */
  return (
    partnerContributorAccessLevel === 'read_write' &&
    Boolean(userRoles?.includes(AppRole.PARTNER_CONTRIBUTOR))
  );
};

/**
 * Enhanced custom hook to check both role-based and field-level permissions
 * Returns whether the current user can edit form fields based on their roles and field permissions
 *
 * @param requestStage - Optional request stage (falls back to context stage)
 * @param requestId - Optional request ID (needed for partner contributor access level checks)
 */
export const useFormPermissions = (
  requestStage?: string,
  requestId?: string,
) => {
  const { user, permissionsHydrationVersion } = useAuth();
  const contextStage = useRequestStage();

  // Use provided requestStage if available, otherwise fall back to context
  const effectiveRequestStage = requestStage || contextStage;

  // Reduced logging - only log when there might be an issue (no effective stage in RequestDetail context)
  // console.log('🔐 useFormPermissions called with:', {
  //   providedStage: requestStage,
  //   contextStage,
  //   effectiveStage: effectiveRequestStage,
  //   userRoles: user?.roles
  // });

  // Basic role-based permissions. permissionsHydrationVersion is a dependency (not just `user`)
  // because canEditRequest/canCreateRequest/canViewRequest/canCloseRequest read
  // roleService.activeRolePermissions, which can change after this hook's first render — once
  // login-time API hydration completes, asynchronously, well after `user` itself last changed
  // reference. Without this dependency, a component already mounted at login time would keep
  // showing fallback-derived values for its entire mounted lifetime (Story 1339-2.2 code review).
  //
  // permissionsHydrationVersion is NOT read inside this callback, and the linter can't tell
  // that's intentional: it exists purely to force recomputation when an external mutable
  // singleton (roleService) changes out-of-band. Do NOT remove it just because the linter calls
  // it "unnecessary" — that reintroduces the exact staleness bug this dependency fixes.
  /* eslint-disable react-hooks/exhaustive-deps */
  const rolePermissions = useMemo(
    () => ({
      canEditRequest: roleService.canEditRequest(user),
      canCreateRequest: roleService.canCreateRequest(user),
      canViewRequest: roleService.canViewRequest(user),
      canCloseRequest: roleService.canCloseRequest(user),
      isAdmin: roleService.isAdmin(user),
      isITAdministrator: roleService.isITAdministrator(user),
      user, // Include user for accessing email and other properties
    }),
    [user, permissionsHydrationVersion],
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  // Field-level permissions for team member management (async, stage-aware)
  const [canManageSponsors, setCanManageSponsors] = useState(() =>
    roleService.canManageSponsors(user),
  );
  const [canManageContributors, setCanManageContributors] = useState(() =>
    roleService.canManageContributors(user),
  );
  const [canManagePartnerContributors, setCanManagePartnerContributors] =
    useState(() => roleService.canManageContributors(user));
  const [canReadSponsors, setCanReadSponsors] = useState(() =>
    roleService.canManageSponsors(user),
  );
  const [canReadContributors, setCanReadContributors] = useState(() =>
    roleService.canManageContributors(user),
  );
  const [canReadPartnerContributors, setCanReadPartnerContributors] = useState(
    () => roleService.canManageContributors(user),
  );
  const [sponsorAccess, setSponsorAccess] = useState<FieldAccessResult | null>(
    null,
  );
  const [contributorAccess, setContributorAccess] =
    useState<FieldAccessResult | null>(null);
  const [partnerContributorAccess, setPartnerContributorAccess] =
    useState<FieldAccessResult | null>(null);

  /**
   * Whether this user may change the request's deliverables at this stage.
   *
   * Starts `false` so the section renders read-only until the check resolves — the control
   * should never be offered and then withdrawn.
   *
   * **This value is stage-blind wherever the stage-aware check cannot run**, which today is
   * Request Detail, its only consumer (see the fallback note below). There it answers from
   * the role union, so it returns `true` at Draft for the seven roles whose Deliverables
   * cell is `-`. Do not read it as "may edit at this stage". It is safe as a WRITE gate only
   * because a user who cannot pass `Button - Save` at that stage never gets an Edit button
   * and so never enters edit mode — the chain documented on `DELIVERABLES_FALLBACK_ROLES`.
   *
   * That makes it the wrong input for anything a viewer can see WITHOUT entering edit mode.
   * `RequestDetail` pairs it with `buttonPermissions.canShowEdit` when choosing the
   * read-only subtitle, because keying copy off this value alone told those seven roles to
   * press an Edit button that does not exist.
   *
   * The underlying matrix row is an ordinary field's (`Racks > Rack Height` is the
   * reference): at Site Assessment and Configuration everyone who can edit the request can
   * edit deliverables, at Draft only the two requestor roles can, and on a Closed request
   * nobody can (FR18 keeps the section visible and readable there). That is what the DATA
   * says; this flag reflects it only where the stage is known.
   */
  const [canEditDeliverables, setCanEditDeliverables] = useState(false);

  useEffect(() => {
    /**
     * Used by the two branches below that return before the stage-aware check can run.
     * Without it `canEditDeliverables` stays `false` forever whenever the stage has not
     * resolved — and on Request Detail it never resolves, so this is that screen's whole
     * answer. See `canFallBackToEditingDeliverables` above for what keeps that safe.
     */
    const deliverablesFallback = async () => {
      // Cheap path first: a role in the union needs no lookup at all, so the common case
      // costs nothing and a failure below cannot take an already-granted role away.
      if (canFallBackToEditingDeliverables(user?.roles)) {
        return true;
      }

      // Only a Partner Contributor has a per-request access level, so only they need the
      // lookup. The service caches by `requestId:userEmail`, and Request Detail preloads it
      // for exactly this kind of consumer, so this is usually a cache hit.
      if (
        !user?.roles?.includes(AppRole.PARTNER_CONTRIBUTOR) ||
        !requestId ||
        !user?.email
      ) {
        return false;
      }

      try {
        const accessLevel =
          await teamInformationService.getPartnerContributorAccessLevel(
            requestId,
            user.email,
          );
        return canFallBackToEditingDeliverables(user.roles, accessLevel);
      } catch (error) {
        // Fail closed. An unresolved access level must not hand out write access, and the
        // section stays read-only, which is the pre-existing behaviour rather than a new one.
        console.error(
          'Failed to resolve partner contributor access level for deliverables:',
          error,
        );
        return false;
      }
    };

    const checkTeamPermissions = async () => {
      if (!user?.roles) {
        const defaultCanManageSponsors = roleService.canManageSponsors(user);
        const defaultCanManageContributors =
          roleService.canManageContributors(user);
        setCanManageSponsors(defaultCanManageSponsors);
        setCanManageContributors(defaultCanManageContributors);
        setCanManagePartnerContributors(defaultCanManageContributors);
        setCanReadSponsors(defaultCanManageSponsors);
        setCanReadContributors(defaultCanManageContributors);
        setCanReadPartnerContributors(defaultCanManageContributors);
        setCanEditDeliverables(await deliverablesFallback());
        return;
      }

      // If no request stage, fall back to role-based permissions
      if (!effectiveRequestStage) {
        const defaultCanManageSponsors = roleService.canManageSponsors(user);
        const defaultCanManageContributors =
          roleService.canManageContributors(user);
        setCanManageSponsors(defaultCanManageSponsors);
        setCanManageContributors(defaultCanManageContributors);
        setCanManagePartnerContributors(defaultCanManageContributors);
        setCanReadSponsors(defaultCanManageSponsors);
        setCanReadContributors(defaultCanManageContributors);
        setCanReadPartnerContributors(defaultCanManageContributors);
        setCanEditDeliverables(await deliverablesFallback());
        return;
      }

      // console.log('🔐 useFormPermissions: Checking field permissions for stage:', effectiveRequestStage);

      // Check field permission for "Vertiv Sponsors"
      const sponsorAccessResult = await fieldPermissionService.checkFieldAccess(
        {
          userRoles: user.roles,
          section: 'Opportunity Information',
          field: 'Vertiv Sponsors',
          requestStage: effectiveRequestStage,
          requestId: requestId,
          userEmail: user.email,
        },
      );
      // console.log('🔐 useFormPermissions: Vertiv Sponsors access:', sponsorAccessResult);
      setCanManageSponsors(sponsorAccessResult.canWrite);
      setCanReadSponsors(sponsorAccessResult.canRead);
      setSponsorAccess(sponsorAccessResult);

      // Check field permission for "Vertiv Contributors"
      const contributorAccessResult =
        await fieldPermissionService.checkFieldAccess({
          userRoles: user.roles,
          section: 'Opportunity Information',
          field: 'Vertiv Contributors',
          requestStage: effectiveRequestStage,
          requestId: requestId,
          userEmail: user.email,
        });
      // console.log('🔐 useFormPermissions: Vertiv Contributors access:', contributorAccessResult);
      setCanManageContributors(contributorAccessResult.canWrite);
      setCanReadContributors(contributorAccessResult.canRead);
      setContributorAccess(contributorAccessResult);

      // Check field permission for "Partner Contributors"
      const partnerAccessResult = await fieldPermissionService.checkFieldAccess(
        {
          userRoles: user.roles,
          section: 'Opportunity Information',
          field: 'Partner Contributors',
          requestStage: effectiveRequestStage,
          requestId: requestId,
          userEmail: user.email,
        },
      );
      setCanManagePartnerContributors(partnerAccessResult.canWrite);
      setCanReadPartnerContributors(partnerAccessResult.canRead);
      setPartnerContributorAccess(partnerAccessResult);

      // Deliverables — an ordinary request field, so access to the request is the gate.
      // Note this branch is unreachable from Request Detail, which never has a stage; see
      // `canFallBackToEditingDeliverables`.
      const deliverablesAccessResult =
        await fieldPermissionService.checkFieldAccess({
          userRoles: user.roles,
          section: 'Deliverables',
          field: 'Deliverables',
          requestStage: effectiveRequestStage,
          requestId: requestId,
          userEmail: user.email,
        });
      setCanEditDeliverables(deliverablesAccessResult.canWrite);
    };

    checkTeamPermissions();
  }, [user, user?.roles, user?.email, effectiveRequestStage, requestId]);

  // Field-level permission functions
  const checkFieldAccess = useCallback(
    async (section: string, field: string): Promise<FieldAccessResult> => {
      if (!user?.roles) {
        return { canRead: false, canWrite: false, isHidden: true };
      }

      return await fieldPermissionService.checkFieldAccess({
        userRoles: user.roles,
        section,
        field,
        requestStage: effectiveRequestStage,
        requestId: requestId,
        userEmail: user.email,
      });
    },
    [user?.roles, user?.email, effectiveRequestStage, requestId],
  );

  const canReadField = useCallback(
    async (section: string, field: string): Promise<boolean> => {
      if (!user?.roles) return false;

      const access = await fieldPermissionService.checkFieldAccess({
        userRoles: user.roles,
        section,
        field,
        requestStage: effectiveRequestStage,
        requestId: requestId,
        userEmail: user.email,
      });
      return access.canRead;
    },
    [user?.roles, user?.email, effectiveRequestStage, requestId],
  );

  const canWriteField = useCallback(
    async (section: string, field: string): Promise<boolean> => {
      if (!user?.roles) return false;

      const access = await fieldPermissionService.checkFieldAccess({
        userRoles: user.roles,
        section,
        field,
        requestStage: effectiveRequestStage,
        requestId: requestId,
        userEmail: user.email,
      });
      return access.canWrite;
    },
    [user?.roles, user?.email, effectiveRequestStage, requestId],
  );

  const isFieldHidden = useCallback(
    async (section: string, field: string): Promise<boolean> => {
      if (!user?.roles) return true;

      const access = await fieldPermissionService.checkFieldAccess({
        userRoles: user.roles,
        section,
        field,
        requestStage: effectiveRequestStage,
        requestId: requestId,
        userEmail: user.email,
      });
      return access.isHidden;
    },
    [user?.roles, user?.email, effectiveRequestStage, requestId],
  );

  const getAccessibleSections = useCallback(async (): Promise<string[]> => {
    if (!user?.roles) return [];

    return await fieldPermissionService.getAccessibleSections(
      user.roles,
      effectiveRequestStage,
    );
  }, [user?.roles, effectiveRequestStage]);

  const getAccessibleFields = useCallback(
    async (
      section: string,
    ): Promise<{ fieldName: string; access: FieldAccessResult }[]> => {
      if (!user?.roles) return [];

      return await fieldPermissionService.getAccessibleFields(
        user.roles,
        section,
        effectiveRequestStage,
        requestId,
        user.email,
      );
    },
    [user?.roles, user?.email, effectiveRequestStage, requestId],
  );

  const hasSectionAccess = useCallback(
    async (section: string): Promise<boolean> => {
      if (!user?.roles) return false;

      return await fieldPermissionService.hasSectionAccess(
        user.roles,
        section,
        effectiveRequestStage,
      );
    },
    [user?.roles, effectiveRequestStage],
  );

  return {
    // Role-based permissions (existing)
    ...rolePermissions,

    // Team member management permissions (stage-aware)
    canManageSponsors,
    canManageContributors,
    canManagePartnerContributors,
    canReadSponsors,
    canReadContributors,
    canReadPartnerContributors,
    sponsorAccess,
    contributorAccess,
    partnerContributorAccess,
    canEditDeliverables,

    // Field-level permissions (new)
    checkFieldAccess,
    canReadField,
    canWriteField,
    isFieldHidden,
    getAccessibleSections,
    getAccessibleFields,
    hasSectionAccess,

    // Utility functions
    userRoles: user?.roles || [],
    hasFieldPermissions: Boolean(user?.roles?.length),
  };
};
